import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Grid, TextField, Typography } from "@mui/material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { openSnackbar } from "../../../redux/slices/snackbarSlice";
import ConfirmationModal from "../../../components/custom-modal/ConfirmationModal";
import {
  deleteRepresentant,
  DeleteRepresentantError,
  fetchRepresentantSites,
  RepresentantSites,
  setRepresentantStatus,
} from "../slices/representantSitesSlice";
import {
  RepresentantCreateDialog,
  RepresentantSitesDialog,
  RepresentantSitesTable,
} from "../components";

/** Boîte de confirmation en cours : suppression, proposition d'archivage, archivage ou réactivation. */
type PendingAction =
  | { kind: "delete"; representant: RepresentantSites }
  | { kind: "archiveInstead"; representant: RepresentantSites; preorderCount: number }
  | { kind: "archive"; representant: RepresentantSites }
  | { kind: "unarchive"; representant: RepresentantSites };

/** Administration : représentants, création, archivage, suppression et affectation des sites Sage. */
const RepresentantSitesPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const { representants, loading, error } = useAppSelector(
    (state) => state.representantSitesSlice
  );

  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<RepresentantSites | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => {
    dispatch(fetchRepresentantSites());
  }, [dispatch]);

  const notify = useCallback(
    (message: string, type: "success" | "error") =>
      dispatch(
        openSnackbar({
          message,
          type,
          horizontal: direction === "rtl" ? "left" : "right",
        })
      ),
    [dispatch, direction]
  );

  const filteredRows = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return representants;
    return representants.filter(
      (r) =>
        r.username.toLowerCase().includes(term) ||
        (r.businessId || "").toLowerCase().includes(term) ||
        r.sitesVente.some((code) => code.toLowerCase().includes(term)) ||
        r.sitesExpedition.some((code) => code.toLowerCase().includes(term))
    );
  }, [filter, representants]);

  // Demande de suppression : si des précommandes existent déjà, on propose directement l'archivage.
  const handleDeleteRequest = (representant: RepresentantSites) => {
    if (representant.preorderCount > 0) {
      setPending({ kind: "archiveInstead", representant, preorderCount: representant.preorderCount });
    } else {
      setPending({ kind: "delete", representant });
    }
  };

  const handleToggleStatus = (representant: RepresentantSites) => {
    setPending({ kind: representant.isActive ? "archive" : "unarchive", representant });
  };

  const archive = async (representant: RepresentantSites, isActive: boolean) => {
    try {
      await dispatch(setRepresentantStatus({ userId: representant.userId, isActive })).unwrap();
      notify(
        isActive
          ? t("administration.representantSites.unarchived")
          : t("administration.representantSites.archived"),
        "success"
      );
    } catch (err) {
      notify(
        typeof err === "string" ? err : t("administration.representantSites.statusError"),
        "error"
      );
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const action = pending;
    setPending(null);

    if (action.kind === "delete") {
      try {
        await dispatch(deleteRepresentant(action.representant.userId)).unwrap();
        notify(t("administration.representantSites.deleted"), "success");
      } catch (err) {
        // unwrap() rejette avec la valeur de rejectWithValue (DeleteRepresentantError).
        const rejected = err as DeleteRepresentantError | undefined;
        if (rejected?.conflict) {
          // Vérification côté serveur : des précommandes existent, on propose l'archivage.
          setPending({
            kind: "archiveInstead",
            representant: action.representant,
            preorderCount: rejected.preorderCount,
          });
        } else {
          notify(rejected?.message || t("administration.representantSites.deleteError"), "error");
        }
      }
      return;
    }

    await archive(action.representant, action.kind === "unarchive");
  };

  const confirmTitle = pending
    ? {
        delete: t("administration.representantSites.deleteTitle"),
        archiveInstead: t("administration.representantSites.archiveInsteadTitle"),
        archive: t("administration.representantSites.archiveTitle"),
        unarchive: t("administration.representantSites.unarchiveTitle"),
      }[pending.kind]
    : "";

  const confirmContent = pending
    ? {
        delete: t("administration.representantSites.deleteContent", {
          name: pending.representant.username,
        }),
        archiveInstead: t("administration.representantSites.archiveInsteadContent", {
          name: pending.representant.username,
          count: pending.kind === "archiveInstead" ? pending.preorderCount : 0,
        }),
        archive: t("administration.representantSites.archiveContent", {
          name: pending.representant.username,
        }),
        unarchive: t("administration.representantSites.unarchiveContent", {
          name: pending.representant.username,
        }),
      }[pending.kind]
    : "";

  return (
    <>
      <Grid container display="flex" justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={12} md={6}>
          <Typography variant="h5">
            {t("administration.representantSites.title")}
          </Typography>
        </Grid>
        <Grid
          item
          display="flex"
          justifyContent="end"
          alignItems="center"
          xs={12}
          sm={12}
          md={6}
        >
          <Button
            sx={{ width: "fit-content" }}
            variant="contained"
            startIcon={<ControlPointIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t("administration.representantSites.create")}
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          py: 3,
          px: 2,
          mt: 2,
          borderRadius: "12px",
          border: "1px solid #E8EBFD",
        }}
      >
        <Grid item xs={12} md={6}>
          <TextField
            label={t("administration.representantSites.search")}
            variant="outlined"
            fullWidth
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <RepresentantSitesTable
              rows={filteredRows}
              onEdit={setSelected}
              onDelete={handleDeleteRequest}
              onToggleStatus={handleToggleStatus}
            />
          )}
        </Grid>
      </Grid>
      <RepresentantSitesDialog
        open={selected !== null}
        representant={selected}
        onClose={() => setSelected(null)}
      />
      <RepresentantCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <ConfirmationModal
        open={pending !== null}
        handleClose={() => setPending(null)}
        handleConfirm={handleConfirm}
        title={confirmTitle}
        content={confirmContent}
      />
    </>
  );
};

export default RepresentantSitesPage;
